import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import ProductAttributesEditor from '../../components/ProductAttributesEditor';
import BarcodePrintModal from '../../components/BarcodePrintModal';
import { ProductAttributeField, ProductAttributes, ProductCategory } from '../../types';
import { productService, CreateProductWithItemsRequest } from '../../services/productService';
import { attributeService } from '../../services/attributeService';
import unitService, { Unit } from '../../services/unitService';
import { supplierService } from '../../services/supplierService';
import taxService, { Tax } from '../../services/taxService';
import { useCurrency } from '../../hooks/useCurrency';
import { parseApiError } from '../../utils/errorUtils';
import '../PurchaseItems/PurchaseItemPage.css';
import { Tag, Ruler, ShoppingBag, ShoppingCart, ArrowLeft, Save } from 'lucide-react';

const MAX_LENGTHS = {
  name: 100,
  description: 200,
};

const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'INR', 'LKR'];

interface FieldErrors {
  name?: string;
  categoryId?: string;
  unitOfMeasure?: string;
  description?: string;
  reorderLevel?: string;
  reorderQuantity?: string;
  sellingPrice?: string;
  minimumSellingPrice?: string;
  supplierId?: string;
  purchasePrice?: string;
  leadTimeDays?: string;
}

const ProductCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const defaultCurrency = useCurrency();

  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [salesTaxes, setSalesTaxes] = useState<Tax[]>([]);
  const [purchaseTaxes, setPurchaseTaxes] = useState<Tax[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [attrForm, setAttrForm] = useState<ProductAttributes | null>(null);
  const [attrValues, setAttrValues] = useState<Record<number, string>>({});
  const [attrErrors, setAttrErrors] = useState<Record<number, string>>({});
  const [createSalesItem, setCreateSalesItem] = useState(true);
  const [createPurchaseItem, setCreatePurchaseItem] = useState(true);
  // Set after a successful save — offers barcode label printing before leaving.
  const [createdProduct, setCreatedProduct] = useState<{ id: number; name: string } | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryId: 0,
    unitOfMeasure: '',
    reorderLevel: 0,
    reorderQuantity: 0,
  });

  const [salesData, setSalesData] = useState<{
    sellingPrice: number;
    minimumSellingPrice?: number;
    salesCurrency: string;
    salesChannel: string;
    discountAllowed: boolean;
    returnAllowed: boolean;
    returnPeriodDays: number;
    salesTaxId?: number;
  }>({
    sellingPrice: 0,
    minimumSellingPrice: undefined,
    salesCurrency: defaultCurrency,
    salesChannel: 'Store',
    discountAllowed: true,
    returnAllowed: true,
    returnPeriodDays: 30,
    salesTaxId: undefined,
  });

  const [purchaseData, setPurchaseData] = useState<{
    supplierId: number;
    purchasePrice: number;
    currency: string;
    leadTimeDays: number;
    minimumOrderQuantity: number;
    supplierItemCode: string;
    purchaseTaxId?: number;
  }>({
    supplierId: 0,
    purchasePrice: 0,
    currency: defaultCurrency,
    leadTimeDays: 0,
    minimumOrderQuantity: 0,
    supplierItemCode: '',
    purchaseTaxId: undefined,
  });

  useEffect(() => {
    loadMasterData();
    taxService.getTaxes(1, 200).then(res => {
      const list: Tax[] = res?.data ?? [];
      setSalesTaxes(list.filter(tx => tx.isActive && (tx.applicableFor === 'Sales' || tx.applicableFor === 'Both')));
      setPurchaseTaxes(list.filter(tx => tx.isActive && (tx.applicableFor === 'Purchase' || tx.applicableFor === 'Both')));
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSalesData(prev => ({ ...prev, salesCurrency: defaultCurrency }));
    setPurchaseData(prev => ({ ...prev, currency: defaultCurrency }));
  }, [defaultCurrency]);

  const loadMasterData = async () => {
    try {
      const [categoriesRes, unitsRes, suppliersRes, formRes] = await Promise.all([
        productService.getCategories({ pageSize: 100 }),
        unitService.getUnits(1, 200),
        supplierService.getSuppliers({ pageSize: 1000 }),
        attributeService.getForm().catch(() => null),
      ]);
      if (categoriesRes.data.data) setCategories(categoriesRes.data.data as ProductCategory[]);
      if (formRes?.data?.data) setAttrForm(formRes.data.data as ProductAttributes);
      if (unitsRes) {
        const arr = Array.isArray(unitsRes) ? unitsRes : (unitsRes as any)?.data;
        if (arr) setUnits(arr);
      }
      if (suppliersRes.data.data) setSuppliers(suppliersRes.data.data);
    } catch {
      console.error(t('product.failedToLoadCategories'));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'reorderLevel' || name === 'reorderQuantity') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
      setFieldErrors(prev => ({ ...prev, [name]: undefined }));
      return;
    }
    if (name === 'categoryId') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
      setFieldErrors(prev => ({ ...prev, [name]: undefined }));
      return;
    }
    let finalValue = value;
    if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
      finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
    }
    setFormData(prev => ({ ...prev, [name]: finalValue }));
    setFieldErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSalesChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const checked = (e.target as HTMLInputElement).checked;
    setSalesData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked
        : name === 'salesTaxId' ? (parseInt(value) || undefined)
        : name === 'sellingPrice' ? parseFloat(value) || 0
        : name === 'minimumSellingPrice' ? (value === '' ? undefined : parseFloat(value) || 0)
        : value,
    }));
    if (name === 'sellingPrice' || name === 'minimumSellingPrice') {
      setFieldErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handlePurchaseChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setPurchaseData(prev => ({
      ...prev,
      [name]: name === 'purchaseTaxId' ? (parseInt(value) || undefined)
        : name === 'supplierId' || name === 'leadTimeDays' || name === 'minimumOrderQuantity' ? parseInt(value) || 0
        : name === 'purchasePrice' ? parseFloat(value) || 0
        : value,
    }));
    setFieldErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = t('product.formValidation.productNameRequired'); isValid = false;
    } else if (formData.name.length > MAX_LENGTHS.name) {
      errors.name = t('product.formValidation.productNameMaxLength'); isValid = false;
    }
    if (!formData.categoryId) {
      errors.categoryId = t('product.formValidation.categoryRequired'); isValid = false;
    }
    if (!formData.unitOfMeasure) {
      errors.unitOfMeasure = t('product.formValidation.unitOfMeasureRequired'); isValid = false;
    }
    if (formData.description && formData.description.length > MAX_LENGTHS.description) {
      errors.description = t('product.formValidation.descriptionMaxLength'); isValid = false;
    }
    if (formData.reorderLevel < 0) {
      errors.reorderLevel = t('product.formValidation.reorderLevelInvalid'); isValid = false;
    }
    if (formData.reorderQuantity < 0) {
      errors.reorderQuantity = t('product.formValidation.reorderQuantityInvalid'); isValid = false;
    }
    if (createSalesItem && salesData.sellingPrice < 0) {
      errors.sellingPrice = t('salesItem.formValidation.sellingPriceNegative'); isValid = false;
    }
    if (createSalesItem && salesData.minimumSellingPrice !== undefined) {
      if (salesData.minimumSellingPrice < 0) {
        errors.minimumSellingPrice = t('salesItem.formValidation.minimumSellingPriceNegative'); isValid = false;
      } else if (salesData.minimumSellingPrice > salesData.sellingPrice) {
        errors.minimumSellingPrice = t('salesItem.formValidation.minPriceExceedsSelling'); isValid = false;
      }
    }
    if (createPurchaseItem && !purchaseData.supplierId) {
      errors.supplierId = t('purchaseItem.formValidation.supplierRequired'); isValid = false;
    }
    if (createPurchaseItem && purchaseData.purchasePrice <= 0) {
      errors.purchasePrice = t('purchaseItem.formValidation.purchasePriceRequired'); isValid = false;
    }
    if (createPurchaseItem && purchaseData.leadTimeDays < 0) {
      errors.leadTimeDays = t('purchaseItem.formValidation.leadTimeInvalid'); isValid = false;
    }

    setFieldErrors(errors);
    if (!isValid) {
      const firstError = Object.values(errors).find(e => e);
      if (firstError) setError(firstError);
    }
    return isValid;
  };

  // Custom attributes shown for the currently selected category:
  // general ones always, category-scoped ones only on a category match.
  const visibleCustomAttributes: ProductAttributeField[] = (attrForm?.customAttributes ?? []).filter(
    (a) => a.appliesToAllProducts || (formData.categoryId > 0 && a.categoryIds.includes(formData.categoryId))
  );

  // Values typed for attributes of another category are kept in state (and
  // restored when switching back) but are never saved — surface a notice.
  const hasHiddenValues = (attrForm?.customAttributes ?? []).some(
    (a) => !visibleCustomAttributes.includes(a) && (attrValues[a.attributeDefinitionId] ?? '').trim() !== ''
  );

  const visibleAttributes: ProductAttributeField[] = [
    ...(attrForm?.moduleAttributes ?? []),
    ...visibleCustomAttributes,
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!validateForm()) return;

    // Required attributes (module block + applicable custom) must be filled
    const missing: Record<number, string> = {};
    for (const attr of visibleAttributes) {
      if (attr.isRequired && !(attrValues[attr.attributeDefinitionId] ?? '').trim()) {
        missing[attr.attributeDefinitionId] = t('attribute.valueRequired', { name: attr.name });
      }
    }
    setAttrErrors(missing);
    if (Object.keys(missing).length > 0) {
      setError(Object.values(missing)[0]);
      return;
    }

    setSaving(true);
    try {
      const attributeValues = visibleAttributes
        .filter((attr) => (attrValues[attr.attributeDefinitionId] ?? '').trim() !== '')
        .map((attr) => ({
          attributeDefinitionId: attr.attributeDefinitionId,
          value: attrValues[attr.attributeDefinitionId].trim(),
        }));

      const payload: CreateProductWithItemsRequest = {
        ...formData,
        categoryId: formData.categoryId || undefined,
        attributeValues,
        salesItem: createSalesItem ? {
          sellingPrice: salesData.sellingPrice,
          minimumSellingPrice: salesData.minimumSellingPrice,
          salesCurrency: salesData.salesCurrency,
          salesChannel: salesData.salesChannel,
          discountAllowed: salesData.discountAllowed,
          returnAllowed: salesData.returnAllowed,
          returnPeriodDays: salesData.returnPeriodDays,
          salesTaxId: salesData.salesTaxId || undefined,
        } : undefined,
        purchaseItem: createPurchaseItem ? {
          supplierId: purchaseData.supplierId,
          purchasePrice: purchaseData.purchasePrice,
          purchaseCurrency: purchaseData.currency,
          leadTimeDays: purchaseData.leadTimeDays,
          minimumOrderQuantity: purchaseData.minimumOrderQuantity,
          supplierItemCode: purchaseData.supplierItemCode,
          purchaseTaxId: purchaseData.purchaseTaxId || undefined,
        } : undefined,
      };
      const createRes = await productService.createProductWithItems(payload);
      const createdId: number | undefined = createRes.data?.data?.product?.id;
      if (createdId) {
        // Offer barcode label printing; navigation happens when the dialog closes.
        setCreatedProduct({ id: createdId, name: formData.name });
        setSaving(false);
        return;
      }
      navigate('/products', { state: { message: t('product.productCreatedSuccess') } });
    } catch (err: unknown) {
      setError(parseApiError(err, t('product.failedToCreateProduct')).message);
      setSaving(false);
    }
  };

  return (
    <Layout>
      {createdProduct && (
        <BarcodePrintModal
          isOpen
          productId={createdProduct.id}
          productName={createdProduct.name}
          onClose={() => navigate('/products', { state: { message: t('product.productCreatedSuccess') } })}
        />
      )}
      <form onSubmit={handleSubmit} className="pi-create-page">

        {/* ── Top bar ── */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button
              type="button"
              className="ims-back-btn"
              onClick={() => navigate('/products')}
            >
              <ArrowLeft size={14} strokeWidth={2} />
              <span>{t('product.backToProducts')}</span>
            </button>
            <h1 className="ims-page-title">{t('product.createTitle')}</h1>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate('/products')}
              disabled={saving}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? (
                <>
                  <span className="ims-btn-spinner" />
                  <span className="pi-btn-full">{t('common.saving')}</span>
                </>
              ) : (
                <>
                  <Save size={16} strokeWidth={1.75} />
                  {t('common.save')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error">
            {error}
            <button
              type="button"
              onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* ── 3-column body ── */}
        <div className="ims-create-body">

          {/* LEFT — Product Details ─────────────────────────────────────────── */}
          <div className="ims-panel-left">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('product.details')}</h2>
              </div>
              <div className="ims-card-body">

                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('product.productName')} <span className="required">*</span>
                    <span className="char-counter">{formData.name.length}/{MAX_LENGTHS.name}</span>
                  </label>
                  <input
                    type="text"
                    className={`ims-form-control${fieldErrors.name ? ' is-invalid' : ''}`}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t('product.productName')}
                    maxLength={MAX_LENGTHS.name}
                  />
                  {fieldErrors.name
                    ? <small className="pi-field-error">{fieldErrors.name}</small>
                    : <small className="ims-form-hint">{t('product.productNameInfo')}</small>}
                </div>

                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('product.description')}
                    <span className="char-counter">{formData.description.length}/{MAX_LENGTHS.description}</span>
                  </label>
                  <textarea
                    className={`ims-form-control${fieldErrors.description ? ' is-invalid' : ''}`}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder={t('product.description')}
                    maxLength={MAX_LENGTHS.description}
                  />
                  {fieldErrors.description
                    ? <small className="pi-field-error">{fieldErrors.description}</small>
                    : <small className="ims-form-hint">{t('product.descriptionInfo')}</small>}
                </div>

                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('product.category')} <span className="required">*</span>
                  </label>
                  <select
                    className={`ims-form-control${fieldErrors.categoryId ? ' is-invalid' : ''}`}
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleInputChange}
                  >
                    <option value={0}>{t('product.selectCategory')}</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  {fieldErrors.categoryId
                    ? <small className="pi-field-error">{fieldErrors.categoryId}</small>
                    : <small className="ims-form-hint">{t('product.categoryInfo')}</small>}
                </div>

                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('product.unitOfMeasure')} <span className="required">*</span>
                  </label>
                  <select
                    className={`ims-form-control${fieldErrors.unitOfMeasure ? ' is-invalid' : ''}`}
                    name="unitOfMeasure"
                    value={formData.unitOfMeasure}
                    onChange={handleInputChange}
                  >
                    <option value="">{t('product.selectUnitOfMeasure')}</option>
                    {units.map(unit => (
                      <option key={unit.unitId} value={unit.unitName}>
                        {unit.unitName} ({unit.unitShortName})
                      </option>
                    ))}
                  </select>
                  {fieldErrors.unitOfMeasure
                    ? <small className="pi-field-error">{fieldErrors.unitOfMeasure}</small>
                    : <small className="ims-form-hint">{t('product.unitInfo')}</small>}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">{t('product.reorderLevel')}</label>
                    <input
                      type="number"
                      className={`ims-form-control${fieldErrors.reorderLevel ? ' is-invalid' : ''}`}
                      name="reorderLevel"
                      value={formData.reorderLevel}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="0"
                    />
                    {fieldErrors.reorderLevel && <small className="pi-field-error">{fieldErrors.reorderLevel}</small>}
                  </div>
                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">{t('product.reorderQuantity')}</label>
                    <input
                      type="number"
                      className={`ims-form-control${fieldErrors.reorderQuantity ? ' is-invalid' : ''}`}
                      name="reorderQuantity"
                      value={formData.reorderQuantity}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="0"
                    />
                    {fieldErrors.reorderQuantity && <small className="pi-field-error">{fieldErrors.reorderQuantity}</small>}
                  </div>
                </div>

              </div>
            </div>


            {/* Custom attributes — user-created; general ones always, category
                ones appear automatically when the matching category is chosen */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('attribute.title')}</h2>
              </div>
              <div className="ims-card-body">
                {hasHiddenValues && (
                  <p className="ims-form-hint" style={{ marginTop: 0 }}>
                    {t('attribute.hiddenValuesNotice')}
                  </p>
                )}
                <ProductAttributesEditor
                  attributes={visibleCustomAttributes}
                  values={attrValues}
                  errors={attrErrors}
                  onValueChange={(defId, value) => {
                    setAttrValues((prev) => ({ ...prev, [defId]: value }));
                    setAttrErrors((prev) => ({ ...prev, [defId]: '' }));
                  }}
                  emptyText={t('attribute.noCustomAttributes')}
                />
              </div>
            </div>
          </div>

          {/* CENTER — Sales & Purchase Items ──────────────────────────────── */}
          <div className="ims-panel-center">

            {/* Sales Item */}
            <div className="ims-card">
              <div className="ims-card-header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ShoppingBag size={15} strokeWidth={1.75} />
                <h2 className="ims-card-title" style={{ margin: 0 }}>{t('salesItem.title')}</h2>
                <label style={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 400, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={createSalesItem}
                    onChange={(e) => setCreateSalesItem(e.target.checked)}
                  />
                  {t('product.createThisItem', 'Create this item')}
                </label>
              </div>
              {createSalesItem && (
              <div className="ims-card-body">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>

                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">
                      {t('salesItem.sellingPrice')} <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      className={`ims-form-control${fieldErrors.sellingPrice ? ' is-invalid' : ''}`}
                      name="sellingPrice"
                      value={salesData.sellingPrice || ''}
                      onChange={handleSalesChange}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                    {fieldErrors.sellingPrice && <small className="pi-field-error">{fieldErrors.sellingPrice}</small>}
                  </div>

                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">{t('salesItem.minimumSellingPrice')}</label>
                    <input
                      type="number"
                      className={`ims-form-control${fieldErrors.minimumSellingPrice ? ' is-invalid' : ''}`}
                      name="minimumSellingPrice"
                      value={salesData.minimumSellingPrice ?? ''}
                      onChange={handleSalesChange}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                    {fieldErrors.minimumSellingPrice
                      ? <small className="pi-field-error">{fieldErrors.minimumSellingPrice}</small>
                      : <small className="form-helper">{t('salesItem.minimumSellingPriceHint')}</small>}
                  </div>

                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">{t('salesItem.currency')} <span className="required">*</span></label>
                    <select className="ims-form-control" name="salesCurrency" value={salesData.salesCurrency} onChange={handleSalesChange}>
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">{t('salesItem.salesChannel')} <span className="required">*</span></label>
                    <select className="ims-form-control" name="salesChannel" value={salesData.salesChannel} onChange={handleSalesChange}>
                      <option value="Store">{t('salesItem.channelStore')}</option>
                      <option value="Online">{t('salesItem.channelOnline')}</option>
                      <option value="Wholesale">{t('salesItem.channelWholesale')}</option>
                      <option value="Distributor">{t('salesItem.channelDistributor')}</option>
                    </select>
                  </div>

                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">{t('salesItem.tax')}</label>
                    <select className="ims-form-control" name="salesTaxId" value={salesData.salesTaxId ?? ''} onChange={handleSalesChange}>
                      <option value="">{t('salesItem.noTax')}</option>
                      {salesTaxes.map(tax => (
                        <option key={tax.taxId} value={tax.taxId}>
                          {tax.taxName} ({tax.taxPercentage}%)
                        </option>
                      ))}
                    </select>
                  </div>

                </div>
              </div>
              )}
            </div>

            {/* Purchase Item */}
            <div className="ims-card" style={{ marginTop: '16px' }}>
              <div className="ims-card-header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ShoppingCart size={15} strokeWidth={1.75} />
                <h2 className="ims-card-title" style={{ margin: 0 }}>{t('purchaseItem.title')}</h2>
                <label style={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 400, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={createPurchaseItem}
                    onChange={(e) => setCreatePurchaseItem(e.target.checked)}
                  />
                  {t('product.createThisItem', 'Create this item')}
                </label>
              </div>
              {createPurchaseItem && (
              <div className="ims-card-body">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>

                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">
                      {t('purchaseItem.supplierId')} <span className="required">*</span>
                    </label>
                    <select
                      className={`ims-form-control${fieldErrors.supplierId ? ' is-invalid' : ''}`}
                      name="supplierId"
                      value={purchaseData.supplierId || ''}
                      onChange={handlePurchaseChange}
                    >
                      <option value="">{t('purchaseItem.selectSupplier')}</option>
                      {suppliers.map(s => (
                        <option key={s.supplierId ?? s.id} value={s.supplierId ?? s.id}>
                          {s.code} — {s.name}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.supplierId && <small className="pi-field-error">{fieldErrors.supplierId}</small>}
                  </div>

                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">
                      {t('purchaseItem.purchasePrice')} <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      className={`ims-form-control${fieldErrors.purchasePrice ? ' is-invalid' : ''}`}
                      name="purchasePrice"
                      value={purchaseData.purchasePrice || ''}
                      onChange={handlePurchaseChange}
                      step="0.01"
                      min="0.01"
                      placeholder="0.00"
                    />
                    {fieldErrors.purchasePrice && <small className="pi-field-error">{fieldErrors.purchasePrice}</small>}
                  </div>

                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">{t('purchaseItem.currency')} <span className="required">*</span></label>
                    <select className="ims-form-control" name="currency" value={purchaseData.currency} onChange={handlePurchaseChange}>
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">{t('purchaseItem.leadTimeDays')}</label>
                    <input
                      type="number"
                      className={`ims-form-control${fieldErrors.leadTimeDays ? ' is-invalid' : ''}`}
                      name="leadTimeDays"
                      value={purchaseData.leadTimeDays}
                      onChange={handlePurchaseChange}
                      min="0"
                      placeholder="0"
                    />
                    {fieldErrors.leadTimeDays && <small className="pi-field-error">{fieldErrors.leadTimeDays}</small>}
                  </div>

                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">{t('purchaseItem.minimumOrderQuantity')}</label>
                    <input
                      type="number"
                      className="ims-form-control"
                      name="minimumOrderQuantity"
                      value={purchaseData.minimumOrderQuantity}
                      onChange={handlePurchaseChange}
                      min="0"
                      placeholder="0"
                    />
                  </div>

                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">{t('purchaseItem.supplierItemCode')}</label>
                    <input
                      type="text"
                      className="ims-form-control"
                      name="supplierItemCode"
                      value={purchaseData.supplierItemCode}
                      onChange={handlePurchaseChange}
                      placeholder="e.g. SUP-001"
                      maxLength={50}
                    />
                  </div>

                  <div className="ims-form-group" style={{ margin: 0 }}>
                    <label className="ims-form-label">{t('purchaseItem.tax')}</label>
                    <select className="ims-form-control" name="purchaseTaxId" value={purchaseData.purchaseTaxId ?? ''} onChange={handlePurchaseChange}>
                      <option value="">{t('purchaseItem.noTax')}</option>
                      {purchaseTaxes.map(tax => (
                        <option key={tax.taxId} value={tax.taxId}>
                          {tax.taxName} ({tax.taxPercentage}%)
                        </option>
                      ))}
                    </select>
                  </div>

                </div>
              </div>
              )}
            </div>

            {/* Module attributes — e.g. Vehicle Fitment for AutoMobile companies */}
            {attrForm && attrForm.moduleAttributes.length > 0 && (
              <div className="ims-card" style={{ marginTop: '16px' }}>
                <div className="ims-card-header">
                  <h2 className="ims-card-title">
                    {attrForm.moduleSetName || t('attribute.moduleSection')}
                  </h2>
                </div>
                <div className="ims-card-body">
                  <ProductAttributesEditor
                    attributes={attrForm.moduleAttributes}
                    values={attrValues}
                    errors={attrErrors}
                    columns={2}
                    onValueChange={(defId, value) => {
                      setAttrValues((prev) => ({ ...prev, [defId]: value }));
                      setAttrErrors((prev) => ({ ...prev, [defId]: '' }));
                    }}
                  />
                </div>
              </div>
            )}

          </div>

          {/* RIGHT — Summary + Help ─────────────────────────────────────────── */}
          <div className="ims-panel-right">

            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('product.requiredFields')}</h3>
              </div>
              <div className="ims-card-body">
                <ul className="ims-help-list">
                  <li>{t('product.productName')}</li>
                  <li>{t('product.category')}</li>
                  <li>{t('product.unitOfMeasure')}</li>
                  <li>{t('salesItem.sellingPrice')}</li>
                  <li>{t('purchaseItem.supplierId')}</li>
                  <li>{t('purchaseItem.purchasePrice')}</li>
                </ul>
                <hr style={{ margin: '8px 0', borderColor: '#f3f4f6' }} />
                <p style={{ fontSize: 11, fontWeight: 600, color: '#374151', margin: '8px 0 4px', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                  {t('product.optionalFields')}
                </p>
                <ul className="ims-help-list">
                  <li>{t('product.description')}</li>
                  <li>{t('product.reorderLevel')}</li>
                  <li>{t('product.reorderQuantity')}</li>
                  <li>{t('purchaseItem.leadTimeDays')}</li>
                  <li>{t('purchaseItem.minimumOrderQuantity')}</li>
                  <li>{t('purchaseItem.supplierItemCode')}</li>
                </ul>
              </div>
            </div>

            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('product.sku')}</h3>
              </div>
              <div className="ims-card-body">
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  {t('product.skuAutoGenerated')}
                </small>
              </div>
            </div>

            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('product.relatedItems')}</h3>
              </div>
              <div className="ims-card-body">
                <button type="button" className="detail-quick-link" onClick={() => navigate('/purchase-items')} style={{ width: '100%', textAlign: 'left', marginBottom: '8px' }}>
                  <ShoppingCart size={16} strokeWidth={1.75} />
                  {t('purchaseItem.title')}
                </button>
                <button type="button" className="detail-quick-link" onClick={() => navigate('/sales-items')} style={{ width: '100%', textAlign: 'left', marginBottom: '8px' }}>
                  <ShoppingBag size={16} strokeWidth={1.75} />
                  {t('salesItem.title')}
                </button>
                <button type="button" className="detail-quick-link" onClick={() => navigate('/products/categories')} style={{ width: '100%', textAlign: 'left', marginBottom: '8px' }}>
                  <Tag size={16} strokeWidth={1.75} />
                  {t('productCategory.title')}
                </button>
                <button type="button" className="detail-quick-link" onClick={() => navigate('/products/units')} style={{ width: '100%', textAlign: 'left' }}>
                  <Ruler size={16} strokeWidth={1.75} />
                  {t('unit.title')}
                </button>
              </div>
            </div>

          </div>

        </div>
      </form>
    </Layout>
  );
};

export default ProductCreatePage;
